import * as React from 'react';
import { Link } from 'react-router-dom';
import './WalletPanel.css';
import { useAppKitAccount, useAppKit, useAppKitNetwork, useDisconnect } from "@reown/appkit/react";
import { mainnet, solana } from '@reown/appkit/networks';
import { useBalance } from 'wagmi';
import { type Address } from 'viem';
import { nftService, NFTMetadata } from '../../services';
import { heliusService } from '../../services';
import useMediaQuery from '../../hooks/useMediaQuery';

const { useState, useEffect, useCallback } = React;

// Add SVG components for network logos
const SolanaLogo = () => (
  <svg width="20" height="16" viewBox="0 0 397 311" fill="none" xmlns="http://www.w3.org/2000/svg" className="network-logo">
    <path d="M64.6582 237.353C67.3406 234.674 71.0449 233.154 74.9315 233.154H390.626C396.822 233.154 399.92 240.592 395.518 244.993L332.238 308.246C329.556 310.925 325.852 312.445 321.965 312.445H6.27049C0.0747263 312.445 -3.02323 305.007 1.37863 300.606L64.6582 237.353Z" fill="url(#solana-gradient1)"/>
    <path d="M64.6582 3.7533C67.3406 1.07391 71.0449 -0.445557 74.9315 -0.445557H390.626C396.822 -0.445557 399.92 6.99232 395.518 11.3935L332.238 74.6466C329.556 77.326 325.852 78.8455 321.965 78.8455H6.27049C0.0747263 78.8455 -3.02323 71.4076 1.37863 67.0064L64.6582 3.7533Z" fill="url(#solana-gradient1)"/>
    <path d="M332.238 120.443C329.556 117.764 325.852 116.244 321.965 116.244H6.27049C0.0747263 116.244 -3.02323 123.682 1.37863 128.083L64.6582 191.336C67.3406 194.016 71.0449 195.535 74.9315 195.535H390.626C396.822 195.535 399.92 188.097 395.518 183.696L332.238 120.443Z" fill="url(#solana-gradient1)"/>
    <defs>
      <linearGradient id="solana-gradient1" x1="397" y1="0" x2="0" y2="312.445" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#14F195"/>
        <stop offset="40%" stopColor="#5497D5"/>
        <stop offset="70%" stopColor="#8752F3"/>
        <stop offset="100%" stopColor="#9945FF"/>
      </linearGradient>
    </defs>
  </svg>
);

const EthereumLogo = () => (
  <svg width="16" height="16" viewBox="0 0 256 417" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" className="network-logo">
    <path fill="#343434" d="M127.961 0l-2.795 9.5v275.668l2.795 2.79 127.962-75.638z"/>
    <path fill="#8C8C8C" d="M127.962 0L0 212.32l127.962 75.639V154.158z"/>
    <path fill="#3C3C3B" d="M127.961 312.187l-1.575 1.92v98.199l1.575 4.6L256 236.587z"/>
    <path fill="#8C8C8C" d="M127.962 416.905v-104.72L0 236.585z"/>
    <path fill="#141414" d="M127.961 287.958l127.96-75.637-127.96-58.162z"/>
    <path fill="#393939" d="M0 212.32l127.96 75.638v-133.8z"/>
  </svg>
);

interface WalletPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Collection {
  name: string;
  image: string;
  id: string;
  count: number;
  slug?: string;
}

type Network = 'solana' | 'ethereum';

interface NetworkConnectionStatus {
  solana: boolean;
  ethereum: boolean;
}

// Fix EthereumWindow interface and casting
interface EthereumWindow extends Window {
  ethereum?: {
    request: (args: { method: string; params: any[] }) => Promise<any>;
  };
}

const WalletPanel: React.FC<WalletPanelProps> = ({ isOpen, onClose }) => {
  const { address, isConnected } = useAppKitAccount();
  const appKit = useAppKit();
  const { caipNetwork, switchNetwork } = useAppKitNetwork();
  const [showCopied, setShowCopied] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [walletBalance, setWalletBalance] = useState<string>('0.00000000');
  const [balanceSymbol, setBalanceSymbol] = useState<string>('');
  const [nfts, setNfts] = useState<any[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  
  // Mobile detection hook
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { disconnect } = useDisconnect();
  
  // Cache for NFTs and collections by network
  const [cachedNfts, setCachedNfts] = useState<Record<Network, any[]>>({
    solana: [],
    ethereum: []
  });
  // Add cache for collections by network
  const [cachedCollections, setCachedCollections] = useState<Record<Network, Collection[]>>({
    solana: [],
    ethereum: []
  });
  const [networkDataFetched, setNetworkDataFetched] = useState<Record<Network, boolean>>({
    solana: false,
    ethereum: false
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [currentNetwork, setCurrentNetwork] = useState<Network>('solana');
  // Track connection status for each network
  const [networkStatus, setNetworkStatus] = useState<NetworkConnectionStatus>({
    solana: false, // Initialize both to false instead of assuming solana is connected
    ethereum: false
  });
  const [switchingNetwork, setSwitchingNetwork] = useState(false);
  const [isRefreshingBalance, setIsRefreshingBalance] = useState(false);
  const [isRefreshingNFTs, setIsRefreshingNFTs] = useState(false);
  const [isRefreshingCollections, setIsRefreshingCollections] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Add rate limiting trackers for API calls - 3 per minute
  const apiRateLimits = React.useRef<{
    balance: { lastCalls: number[], pending: boolean },
    nfts: { lastCalls: number[], pending: boolean },
    collections: { lastCalls: number[], pending: boolean }
  }>({
    balance: { lastCalls: [], pending: false },
    nfts: { lastCalls: [], pending: false },
    collections: { lastCalls: [], pending: false }
  });

  // Network switch rate limiting - max 5 per minute
  const networkSwitchRateLimit = React.useRef<{
    lastSwitches: number[],
    pending: boolean
  }>({
    lastSwitches: [],
    pending: false
  });
  
  // Use the wagmi useBalance hook for Ethereum networks
  const { data: balanceData, refetch: refetchBalance } = useBalance({
    address: (currentNetwork === 'ethereum' && address) ? address as Address : undefined,
  });

  // Add this with other state variables
  const apiCallTracker = React.useRef<{
    lastBalanceCheck: number;
    balanceCallCount: number;
    pendingBalanceCall: boolean;
  }>({
    lastBalanceCheck: 0,
    balanceCallCount: 0,
    pendingBalanceCall: false
  });

  // Add this near the top with other state variables
  const isDisconnecting = React.useRef(false);

  // Get current address based on selected network and connection status
  const getCurrentAddress = useCallback(() => {
    if (!isConnected || !address) return '';
    return address;
  }, [isConnected, address]);

  // Handle wallet connect button click for mobile
  const handleWalletConnectClick = useCallback(() => {
    if (isMobile) {
      onClose();
    }
  }, [isMobile, onClose]);

  // Control body overflow when panel is open
  useEffect(() => {
    if (isOpen) {
      // Directly set overflow style instead of using a class
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Copy wallet address to clipboard
  const copyAddressToClipboard = () => {
    const currentAddress = getCurrentAddress();
    if (currentAddress) {
      navigator.clipboard.writeText(currentAddress);
      setShowCopied(true);
      setToast({
        message: "Address copied to clipboard!",
        type: "success"
      });
      setTimeout(() => setToast(null), 5000);
      
      // Reset the checkmark after 2 seconds
      setTimeout(() => {
        setShowCopied(false);
      }, 2000);
    }
  };

  // Format wallet address to show first 10 and last 10 characters
  const formatWalletAddress = (address: string) => {
    if (!address) return '';
    if (address.length <= 25) return address;
    return `${address.substring(0, 12)}...${address.substring(address.length - 12)}`;
  };

  // Handle disconnect
  const handleDisconnect = async () => {
    try {
      // Set disconnecting flag immediately to block all API calls
      isDisconnecting.current = true;
      
      // Show toast notification
      setToast({
        message: "Disconnecting from all wallets...",
        type: "info"
      });
      setTimeout(() => setToast(null), 5000);
      
      // Close panel immediately for better UX
      onClose();
      
      // Disconnect from all networks - make a full reset of connection status
      setNetworkStatus({
        solana: false,
        ethereum: false
      });
      
      // Reset all data
      setCachedNfts({
        solana: [],
        ethereum: []
      });
      setCachedCollections({
        solana: [],
        ethereum: []
      });
      setNetworkDataFetched({
        solana: false,
        ethereum: false
      });
      
      // Reset all UI states
      setNfts([]);
      setCollections([]);
      setWalletBalance('0.00000000');
      setBalanceSymbol('');
      
      // Try to disconnect from Ethereum if possible
      // Use AppKit disconnect function
      await disconnect();
      // Keep the disconnecting flag true for at least 2 seconds to prevent any rogue API calls
      setTimeout(() => {
        isDisconnecting.current = false;
      }, 2000);
    } catch (error) {
      console.error("Error disconnecting wallets:", error);
      setToast({
        message: "Error disconnecting wallets",
        type: "error"
      });
      setTimeout(() => setToast(null), 5000);
      // Reset the disconnecting flag in case of error
      setTimeout(() => {
        isDisconnecting.current = false;
      }, 2000);
    }
  };

  // Create a wrapper for refetchBalance to limit API calls
  const throttledRefetchBalance = useCallback(async () => {
    // If already pending, don't make another call
    if (apiCallTracker.current.pendingBalanceCall) {
      return null;
    }
    
    const now = Date.now();
    const timeSinceLastCall = now - apiCallTracker.current.lastBalanceCheck;
    
    // If we've already made 1 calls and it's been less than 30 seconds, skip
    if (apiCallTracker.current.balanceCallCount >= 2 && timeSinceLastCall < 30000) {
      return null;
    }
    
    // Reset count if it's been more than 30 seconds
    if (timeSinceLastCall > 30000) {
      apiCallTracker.current.balanceCallCount = 0;
    }
    
    // Set pending flag and track the call
    apiCallTracker.current.pendingBalanceCall = true;
    
    try {
      const result = await refetchBalance();
      // Update tracker on success
      apiCallTracker.current.lastBalanceCheck = Date.now();
      apiCallTracker.current.balanceCallCount++;
      apiCallTracker.current.pendingBalanceCall = false;
      
      return result;
    } catch (error) {
      // Reset pending flag on error
      apiCallTracker.current.pendingBalanceCall = false;
      throw error;
    }
  }, [refetchBalance]);

  // Handle network switch using AppKit's switchNetwork function
  const handleNetworkSwitch = (network: Network) => {
    // If already on this network, do nothing
    if (network === currentNetwork) return;
    
    // Check rate limit before proceeding
    if (!checkNetworkSwitchRateLimit()) {
      return;
    }
    
    // If the network isn't connected yet, open the AppKit to connect
    if ((network === 'solana' && !networkStatus.solana && !networkDataFetched.solana) || 
        (network === 'ethereum' && !networkStatus.ethereum && !networkDataFetched.ethereum)) {
      
      // Show toast notification
      setToast({
        message: `Please connect to ${network.charAt(0).toUpperCase() + network.slice(1)} network`,
        type: 'info'
      });
      setTimeout(() => setToast(null), 5000);
      
      // Clear pending flag since we're not actually switching
      clearNetworkSwitchPendingFlag();
      
      // Open the AppKit modal for connection with the correct namespace
      if (appKit && appKit.open) {
        appKit.open({ 
          view: "Connect", 
          namespace: network === 'solana' ? "solana" : "eip155" 
        });
      }
      
      return;
    }
    
    // Set switching state
    setSwitchingNetwork(true);
    setIsLoading(true);
    
    // Clear current NFTs and collections display for switching animation
    setNfts([]);
    setCollections([]);
    
    // Show switching toast
    setToast({
      message: `Switching to ${network.charAt(0).toUpperCase() + network.slice(1)} network`,
      type: 'info'
    });
    setTimeout(() => setToast(null), 5000);
    
    try {
      // Use the appropriate network object based on the selected network
      const targetNetwork = network === 'solana' ? solana : mainnet;
      
      // Directly update the UI state first for better responsiveness
      setCurrentNetwork(network);
      
      // Mark the current network as connected but preserve other connections
      if (network === 'solana') {
        setNetworkStatus(prev => ({
          ...prev,
          solana: true
        }));
      } else {
        setNetworkStatus(prev => ({
          ...prev,
          ethereum: true
        }));
      }
      
      // Call the switchNetwork function with the appropriate network object if available
      if (typeof switchNetwork === 'function') {
          switchNetwork(targetNetwork);
      }
      
      // Check if we already have cached data for this network and display it immediately
      if (network === 'solana' && networkDataFetched.solana) {
        setNfts(cachedNfts.solana);
        // Also restore collections from cache
        if (cachedCollections.solana && cachedCollections.solana.length > 0) {
          setCollections(cachedCollections.solana);
        }
      } else if (network === 'ethereum' && cachedNfts.ethereum && cachedNfts.ethereum.length > 0 && networkDataFetched.ethereum) {
        setNfts(cachedNfts.ethereum);
        // Also restore collections from cache
        if (cachedCollections.ethereum && cachedCollections.ethereum.length > 0) {
          setCollections(cachedCollections.ethereum);
        }
        
        // Also update the balance
        refetchBalance().then(() => {
          if (balanceData) {
            const ethBalance = parseFloat(balanceData.formatted || '0');
            setWalletBalance(ethBalance.toFixed(8));
            setBalanceSymbol(balanceData.symbol || 'ETH');
          }
        });
      } else {
        // If no cached data, mark the network for data fetching
        setNetworkDataFetched(prev => ({
          ...prev,
          [network]: false
        }));
      }
      
      // Clear loading states after a short delay and load data
      setTimeout(() => {
        setSwitchingNetwork(false);
        setIsLoading(false);
        
        // Clear the pending flag
        clearNetworkSwitchPendingFlag();
        
        // Fetch fresh data if needed 
        if (network === 'solana' && !networkDataFetched.solana && address) {
          fetchSolanaData();
        } else if (network === 'ethereum' && !networkDataFetched.ethereum && address) {
          fetchEthereumData();
        }
      }, 1000);
    } catch (error: unknown) {
      console.error("Network switch error:", error);
      setSwitchingNetwork(false);
      setIsLoading(false);
      clearNetworkSwitchPendingFlag();
      setToast({
        message: `Failed to switch to ${network} network`,
        type: 'error'
      });
      setTimeout(() => setToast(null), 5000);
    }
  };

  // Get toast icon based on type
  const getToastIcon = (type: 'success' | 'error' | 'info') => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '✉️';
    }
  };

  // Wrap fetchSolanaBalance in a useCallback with disconnection check
  const fetchSolanaBalance = useCallback(async (address: string, refresh: boolean = false) => {
    // Skip balance fetching completely if we're disconnecting
    if (isDisconnecting.current) {
      return { formatted: '0.00000000', symbol: 'SOL' };
    }
    
    try {
      // Determine the correct network based on caipNetwork
      const networkId = typeof caipNetwork?.caipNetworkId === 'string' ? caipNetwork.caipNetworkId : '';
      const isMainnet = networkId.startsWith('solana');
      const network = isMainnet ? 'mainnet' : 'devnet';
      
      // Use the new Helius service to get the balance with refresh parameter
      const balanceData = await heliusService.getBalance(address, network, refresh);
      
      if (balanceData?.result?.value !== undefined) {
        const formatted = heliusService.formatSolBalance(balanceData.result.value);
        return { 
          formatted: formatted.replace(' SOL', ''), // Remove SOL suffix since we add it separately
          symbol: 'SOL' 
        };
      }
      
      return { formatted: '0.00000000', symbol: 'SOL' };
    } catch (error) {
      return { formatted: '0.00000000', symbol: 'SOL' };
    }
  }, [caipNetwork]);

  // First, define the fetchEthereumData function before any references to it
  const fetchEthereumData = useCallback(async () => {
    
    if (!address || !isConnected || isDisconnecting.current) {
      return;
    }
    
    try {
      setIsLoading(true);
      setIsRefreshingBalance(true);
      setIsRefreshingNFTs(true);
      setIsRefreshingCollections(true);
      
      // Only update network status if it's not already set to true
      // This prevents unnecessary re-renders
      setNetworkStatus(prev => {
        if (prev.ethereum) return prev; // Skip update if already true
        return {
        ...prev,
        ethereum: true
        };
      });
      
      // Fetch Ethereum balance first - use throttled version
      try {
        await throttledRefetchBalance();
        if (balanceData) {
          const ethBalance = parseFloat(balanceData.formatted || '0');
          setWalletBalance(ethBalance.toFixed(8));
          setBalanceSymbol(balanceData.symbol || 'ETH');
        } else {
          setWalletBalance('0.00000000');
          setBalanceSymbol('ETH');
        }
      } catch (error) {
        setWalletBalance('0.00000000');
        setBalanceSymbol('ETH');
      } finally {
        setIsRefreshingBalance(false);
      }
      
      // Fetch NFTs with retry mechanism
      try {
        const nfts = await nftService.getNFTsByAccount(address);
        
        // Use all NFTs, up to 200 (which is the API limit)
        const limitedNfts = nfts.slice(0, 200);
        
        if (limitedNfts.length === 0) {
          // Handle case where no NFTs were found after retries
          setNfts([]);
          setCollections([]);
          setNetworkDataFetched(prev => ({ ...prev, ethereum: true }));
          
          setToast({
            message: "No Ethereum NFTs found or API request failed",
            type: "info"
          });
          setTimeout(() => setToast(null), 5000);
          
          setIsRefreshingNFTs(false);
          setIsRefreshingCollections(false);
          setIsLoading(false);
          return;
        }
        
        // Transform ColorFuel NFT metadata to our NFT format with optimized image handling
        const transformedNfts = limitedNfts.map((nft: NFTMetadata, index: number) => {
          // Prefer display_image_url as recommended
          const imageUrl = nft.display_image_url || nft.image_url || '/assets/placeholder-nft.svg';
          
          return {
            id: `${nft.identifier}-${nft.contract || ''}-${index}`,
            name: nft.name || 'Unnamed NFT',
            image: imageUrl,
            description: nft.description ? nft.description.substring(0, 100) : '',
            symbol: nft.collection,
            externalUrl: nft.opensea_url
          };
        });
        
        // Group NFTs by collection and optimize collection processing
        const collectionMap = new Map<string, { name: string; count: number; image: string; slug: string }>();
        nfts.forEach(nft => {
          const collection = nft.collection;
          if (!collectionMap.has(collection)) {
            // Use display_image_url as primary image source
            const imageUrl = nft.display_image_url || nft.image_url || '/assets/placeholder-nft.svg';
            
            collectionMap.set(collection, {
              name: collection,
              count: 0,
              image: imageUrl,
              slug: collection.toLowerCase().replace(/\s+/g, '-')
            });
          }
          const collectionData = collectionMap.get(collection)!;
          collectionData.count++;
        });
        
        // Show all collections
        const collections = Array.from(collectionMap.entries())
          .sort((a, b) => b[1].count - a[1].count) // Sort by count in descending order
          .map(([name, data], index) => ({
            name: data.name,
            image: data.image,
            id: `ethereum-${name}-${index}`,
            count: data.count,
            slug: data.slug
          }));
        
        // Cache the data first, then update state in a single operation
        setCachedNfts(prev => ({ ...prev, ethereum: transformedNfts }));
        setNetworkDataFetched(prev => ({ ...prev, ethereum: true }));
        
        // Also cache collections data
        setCachedCollections(prev => ({ ...prev, ethereum: collections }));
        
        // Reset to first page when loading new NFTs
        setCurrentPage(1);
        
        // Update UI with the new data
        setNfts(transformedNfts);
        setCollections(collections);    
        setIsRefreshingNFTs(false);
        setIsRefreshingCollections(false);
        setIsLoading(false);
      } catch (error) {
        // Set empty data on error
        setNfts([]);
        setCollections([]);
        setNetworkDataFetched(prev => ({ ...prev, ethereum: true }));
        
        setToast({
          message: "Failed to fetch Ethereum NFTs",
          type: "error"
        });
        setTimeout(() => setToast(null), 5000);
        
        setIsRefreshingNFTs(false);
        setIsRefreshingCollections(false);
        setIsLoading(false);
      }
    } catch (error) {
      setToast({
        message: "Error loading Ethereum data",
        type: "error"
      });
      setTimeout(() => setToast(null), 5000);
      
      setIsLoading(false);
      setIsRefreshingBalance(false);
      setIsRefreshingNFTs(false);
      setIsRefreshingCollections(false);
    }
  }, [address, isConnected, balanceData, throttledRefetchBalance]);

  // Fetch Solana collections and NFTs - add disconnection check
  const fetchSolanaData = useCallback(async () => {
    // Skip fetching completely if we're disconnecting
    if (isDisconnecting.current) {
      return;
    }
    
    const currentAddress = getCurrentAddress();
    if (!currentAddress || currentNetwork !== 'solana') {
      return;
    }

    setIsLoading(true);
    setIsRefreshingBalance(true);
    setIsRefreshingNFTs(true);
    setIsRefreshingCollections(true);
    
    // Only update network status if it's not already set to true
    setNetworkStatus(prev => {
      if (prev.solana) return prev; // Skip update if already true
      return {
      ...prev,
      solana: true
      };
    });
    
    try {
      // Add another check after setting loading states
      if (isDisconnecting.current) {
        return;
      }
      
      // Update balance using the Solana balance fetching function
      const solBalance = await fetchSolanaBalance(currentAddress);
      
      // Skip further processing if disconnecting
      if (isDisconnecting.current) {
        return;
      }
      
      setWalletBalance(solBalance.formatted);
      setBalanceSymbol(solBalance.symbol);
      
      // Fetch collections using the new Helius service
      const networkId = typeof caipNetwork?.caipNetworkId === 'string' ? caipNetwork.caipNetworkId : '';
      const isMainnet = networkId.startsWith('solana');
      const network = isMainnet ? 'mainnet' : 'devnet';
      
      const userCollections = await heliusService.getCollectionsByOwner(currentAddress, network);
      
      // Check again after async operation
      if (isDisconnecting.current) {
        return;
      }
      
      const formattedCollections = userCollections.map((collection, index) => ({
        name: collection.name,
        image: collection.image || '/assets/placeholder-nft.svg',
        id: `solana-${collection.address}-${index}`, // Add network prefix and index for uniqueness
        count: 0 // We'll update this after fetching NFTs
      }));
      
      // Fetch NFTs using the new Helius service - use up to 200 for consistency with Ethereum
      const userNFTs = await heliusService.getNFTsByOwner(currentAddress, network);
      
      // Check again after async operation
      if (isDisconnecting.current) {
        return;
      }
      
      const limitedNFTs = userNFTs.slice(0, 200);
      
      // Update collection counts
      const collectionCounts = new Map<string, number>();
      limitedNFTs.forEach(nft => {
        if (nft.collection) {
          const count = collectionCounts.get(nft.collection) || 0;
          collectionCounts.set(nft.collection, count + 1);
        }
      });
      
      const collectionsWithCounts = formattedCollections.map(collection => ({
        ...collection,
        count: collectionCounts.get(collection.id) || 0
      }));
      
      // One final check before updating state
      if (isDisconnecting.current) {
        return;
      }
      
      // Cache data first
      setCachedNfts(prev => ({
        ...prev,
        solana: limitedNFTs
      }));
      // Also cache collections data
      setCachedCollections(prev => ({
        ...prev,
        solana: collectionsWithCounts
      }));
      setNetworkDataFetched(prev => ({
        ...prev,
        solana: true
      }));
      
      // Reset to first page when loading new NFTs
      setCurrentPage(1);
      
      // Then update the UI state in a single batch
      setNfts(limitedNFTs);
      setCollections(collectionsWithCounts);
      
    } catch (error) {
      setToast({
        message: "Failed to load your Solana data",
        type: "error"
      });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setIsLoading(false);
      setIsRefreshingBalance(false);
      setIsRefreshingNFTs(false);
      setIsRefreshingCollections(false);
    }
  }, [getCurrentAddress, currentNetwork, fetchSolanaBalance, caipNetwork?.caipNetworkId]);

  // Initialize network status based on current connection when component mounts
  useEffect(() => {
    // Only run once on initial load, not on every connection change
    if (!isConnected || !address) {
      return;
    }
    
    // Check if we have caipNetwork info and use it to determine network
    if (caipNetwork && typeof caipNetwork.id === 'string') {
      const networkId = caipNetwork.id;
      if (networkId.startsWith('eip155')) {
        // Connected to Ethereum - ensure Solana is disconnected
        setCurrentNetwork('ethereum');
        
        // Always set a clean network status when connecting after disconnect
        setNetworkStatus({
          solana: false,
          ethereum: true
        });
        
        // Fetch Ethereum data - use throttled version
        throttledRefetchBalance().then(() => {
          if (balanceData) {
            const ethBalance = parseFloat(balanceData.formatted || '0');
            setWalletBalance(ethBalance.toFixed(8));
            setBalanceSymbol(balanceData.symbol || 'ETH');
          }
        }).catch(error => {
          console.error("Error fetching Ethereum balance:", error);
        });
        
        // Small delay before fetching NFTs - only fetch if data not already cached
        if (!networkDataFetched.ethereum) {
          setTimeout(() => {
            fetchEthereumData();
          }, 500);
        }
      }
      else {
        // Connected to Solana - ensure Ethereum is disconnected
        setCurrentNetwork('solana');
        
        // Always set a clean network status when connecting after disconnect
        setNetworkStatus({
          solana: true,
          ethereum: false
        });

        // Also update the balance
        const currentAddress = getCurrentAddress();
        fetchSolanaBalance(currentAddress).then(solBalance => {
          setWalletBalance(solBalance.formatted);
          setBalanceSymbol(solBalance.symbol);
        });
        
        if (!networkDataFetched.solana) {
          setTimeout(() => {
            fetchSolanaData();
          }, 500);
        }
      }
    } else {
      // If we don't have caipNetwork info, try to detect via browser
      const ethereumBrowserWindow = window as unknown as EthereumWindow;
      if (ethereumBrowserWindow.ethereum) {
        // Detect MetaMask or other Ethereum provider
        // Always ensure clean connection status
        setNetworkStatus({
          solana: false,
          ethereum: true
        });
        
        // Set current network to Ethereum
        setCurrentNetwork('ethereum');
        
        // Fetch Ethereum data - use throttled version
        throttledRefetchBalance().then(() => {
          if (balanceData) {
            const ethBalance = parseFloat(balanceData.formatted || '0');
            setWalletBalance(ethBalance.toFixed(8));
            setBalanceSymbol(balanceData.symbol || 'ETH');
          }
        }).catch(error => {
          console.error("Error fetching Ethereum balance:", error);
        });
        
        // Small delay before fetching NFTs
        if (!networkDataFetched.ethereum) {
          setTimeout(() => {
            fetchEthereumData();
          }, 500);
        }
      }
    }
  }, [isConnected, address, caipNetwork, balanceData, currentNetwork, 
      fetchEthereumData, fetchSolanaData, fetchSolanaBalance, getCurrentAddress,
      networkDataFetched.ethereum, networkDataFetched.solana, refetchBalance, throttledRefetchBalance]);

  // Update current network based on caipNetwork from AppKit
  useEffect(() => {
    // Skip if we're switching networks or if no caipNetwork is available
    if (!caipNetwork || switchingNetwork) {
      return;
    }

    const networkId = typeof caipNetwork.caipNetworkId === 'string' ? caipNetwork.caipNetworkId : '';
    // Use a flag to prevent multiple state updates in the same cycle
    let didNetworkChange = false;
    
    // Check if the network ID is a string and starts with 'solana'
    if (networkId.startsWith('solana')) {
      // Only show toast if we're switching from a different network
      if (currentNetwork !== 'solana') {
        didNetworkChange = true;
        
        // Always ensure clean connection status when network changes
        setNetworkStatus({
          solana: true,
          ethereum: false
        });
        
        // Update current network
        setCurrentNetwork('solana');
        
        // Clear existing data
        setNfts([]);
        setCollections([]);
        setNetworkDataFetched(prev => ({
          ...prev,
          solana: false // Force data refresh
        }));
        
        // Show toast notification
        /*setToast({
          message: 'Connected to Solana network',
          type: 'success'
        });
        setTimeout(() => setToast(null), 5000);*/
      }
    } 
    // Check if the network ID is a string and starts with 'eip155' (Ethereum)
    else if (networkId.startsWith('eip155')) {
      // If we're not already on Ethereum network, switch to it
      if (currentNetwork !== 'ethereum') {
        didNetworkChange = true;
        
        // Always ensure clean connection status when network changes
        setNetworkStatus({
          solana: false,
          ethereum: true
        });
        
        // Update current network
        setCurrentNetwork('ethereum');
        
        // Clear data
        setNfts([]);
        setCollections([]);
        setNetworkDataFetched(prev => ({
          ...prev,
          ethereum: false // Reset to force data fetching
        }));

        // Show toast notification
        /*setToast({
          message: 'Connected to Ethereum network',
          type: 'success'
        });
        setTimeout(() => setToast(null), 5000);*/
      }
    }
    
    // Clear loading states if network changed
    if (didNetworkChange) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [caipNetwork, currentNetwork, switchingNetwork, networkStatus]);

  // Create rate limiting check function
  const checkRateLimit = (type: 'balance' | 'nfts' | 'collections'): boolean => {
    const tracker = apiRateLimits.current[type];
    const now = Date.now();
    
    // Remove calls older than 1 minute
    tracker.lastCalls = tracker.lastCalls.filter(time => now - time < 60000);
    
    // Check if we've made 3 or more calls in the last minute
    if (tracker.lastCalls.length >= 3 || tracker.pending) {
      setToast({
        message: `Rate limit reached for ${type}. Please try again later.`,
        type: "info"
      });
      setTimeout(() => setToast(null), 5000);
      return false;
    }
    
    // Add current call to the tracker
    tracker.lastCalls.push(now);
    tracker.pending = true;
    
    return true;
  };

  // Create function to clear pending flag
  const clearPendingFlag = (type: 'balance' | 'nfts' | 'collections') => {
    apiRateLimits.current[type].pending = false;
  };

  // Check network switch rate limit - max 5 per minute
  const checkNetworkSwitchRateLimit = (): boolean => {
    const tracker = networkSwitchRateLimit.current;
    const now = Date.now();
    
    // Remove switches older than 1 minute
    tracker.lastSwitches = tracker.lastSwitches.filter(time => now - time < 60000);
    
    // Check if we've made 5 or more switches in the last minute
    if (tracker.lastSwitches.length >= 5 || tracker.pending) {
      setToast({
        message: `Network switch rate limit reached. Please try again later.`,
        type: "info"
      });
      setTimeout(() => setToast(null), 5000);
      return false;
    }
    
    // Add current switch to the tracker
    tracker.lastSwitches.push(now);
    tracker.pending = true;
    
    return true;
  };

  // Clear network switch pending flag
  const clearNetworkSwitchPendingFlag = () => {
    networkSwitchRateLimit.current.pending = false;
  };

  // Define itemsPerPage here
  const itemsPerPage = 20; // Show 20 NFTs per page

  // Update handleRefreshBalance to honor the rate limits
  const handleRefreshBalance = () => {
    // Check rate limit before proceeding
    if (!checkRateLimit('balance')) {
      return;
    }
    
    setIsRefreshingBalance(true);
    
    if (currentNetwork === 'ethereum' && networkStatus.ethereum) {
      // Reset counter to allow a manual refresh
      apiCallTracker.current.balanceCallCount = 0;
      
      throttledRefetchBalance().then(() => {
        if (balanceData) {
          const ethBalance = parseFloat(balanceData.formatted || '0');
          setWalletBalance(ethBalance.toFixed(8));
          setBalanceSymbol(balanceData.symbol || 'ETH');
          
          setToast({
            message: "Balance refreshed successfully",
            type: "success"
          });
          setTimeout(() => setToast(null), 5000);
        }
      }).catch(error => {
        setToast({
          message: "Failed to refresh balance",
          type: "error"
        });
        setTimeout(() => setToast(null), 5000);
      }).finally(() => {
        setIsRefreshingBalance(false);
        clearPendingFlag('balance');
      });
    } else if (currentNetwork === 'solana' && networkStatus.solana) {
      const address = getCurrentAddress();
      
      fetchSolanaBalance(address, true).then(solBalance => {
        setWalletBalance(solBalance.formatted);
        setBalanceSymbol('SOL');
        
        setToast({
          message: "Balance refreshed successfully",
          type: "success"
        });
        setTimeout(() => setToast(null), 5000);
      }).catch(error => {
        setToast({
          message: "Failed to refresh balance",
          type: "error"
        });
        setTimeout(() => setToast(null), 5000);
      }).finally(() => {
        setIsRefreshingBalance(false);
        clearPendingFlag('balance');
      });
    } else {
      setIsRefreshingBalance(false);
      clearPendingFlag('balance');
    }
  };
  
  // Handle refresh NFTs only
  const handleRefreshNFTs = async () => {
    // Check rate limit before proceeding
    if (!checkRateLimit('nfts')) {
      return;
    }
    
    const currentAddress = getCurrentAddress();
    if (!currentAddress) {
      clearPendingFlag('nfts');
      return;
    }

    setIsRefreshingNFTs(true);
    
    try {
      if (currentNetwork === 'solana' && networkStatus.solana) {
        // Fetch NFTs only using the new Helius service
        const networkId = typeof caipNetwork?.caipNetworkId === 'string' ? caipNetwork.caipNetworkId : '';
        const isMainnet = networkId.startsWith('solana');
        const network = isMainnet ? 'mainnet' : 'devnet';
        
        const userNFTs = await heliusService.getNFTsByOwner(currentAddress, network, true);
        
        // Limit to 200 NFTs
        const limitedNfts = userNFTs.slice(0, 200);
        
        // Update current state
        setNfts(limitedNfts);
        
        // Update cached NFTs
        setCachedNfts(prev => ({
          ...prev,
          solana: limitedNfts
        }));

        setToast({
          message: "NFTs refreshed successfully",
          type: "success"
        });
        setTimeout(() => setToast(null), 5000);

      } else if (currentNetwork === 'ethereum' && networkStatus.ethereum) {
        // Check if we are disconnecting before making API call
        if (isDisconnecting.current) {
          clearPendingFlag('nfts');
          return;
        }
        
        // Fetch NFTs from ColorFuel API with refresh
        const nfts = await nftService.getNFTsByAccount(currentAddress, 'ethereum', 100, true);
        
        // Limit to 200 NFTs
        const limitedNfts = nfts.slice(0, 200);
        
        // Transform ColorFuel NFT metadata to match our NFT interface with performance optimizations
        const transformedNfts = limitedNfts.map((nft: NFTMetadata, index: number) => {
          // Prefer display_image_url as primary image source
          const imageUrl = nft.display_image_url || nft.image_url || '/assets/placeholder-nft.svg';
          
          return {
            id: `${nft.identifier}-${nft.contract || ''}-${index}`,
            name: nft.name || 'Unnamed NFT',
            image: imageUrl,
            description: nft.description ? nft.description.substring(0, 100) : '',
            symbol: nft.collection,
            externalUrl: nft.opensea_url
          };
        });
        
        // Delay rendering to prevent UI freezing
        setTimeout(() => {
        // Update current state
        setNfts(transformedNfts);
        
        // Update cached NFTs
        setCachedNfts(prev => ({
          ...prev,
          ethereum: transformedNfts
        }));
        }, 100);

        setToast({
          message: "NFTs refreshed successfully",
          type: "success"
        });
        setTimeout(() => setToast(null), 5000);
      }
    } catch (error) {
      setToast({
        message: "Failed to refresh NFTs",
        type: "error"
      });
    } finally {
      // Add a small delay before setting isRefreshing to false to ensure UI updates smoothly
      setTimeout(() => {
      setIsRefreshingNFTs(false);
      clearPendingFlag('nfts');
      }, 100);
      setTimeout(() => setToast(null), 5000);
    }
  };
  
  // Handle refresh collections only
  const handleRefreshCollections = async () => {
    // Check rate limit before proceeding
    if (!checkRateLimit('collections')) {
      return;
    }
    
    const currentAddress = getCurrentAddress();
    if (!currentAddress) {
      setToast({
        message: "Please connect your wallet first",
        type: "error"
      });
      setTimeout(() => setToast(null), 5000);
      clearPendingFlag('collections');
      return;
    }

    setIsRefreshingCollections(true);
    
    try {
      if (currentNetwork === 'solana' && networkStatus.solana) {
        // Fetch collections using the new Helius service
        const networkId = typeof caipNetwork?.caipNetworkId === 'string' ? caipNetwork.caipNetworkId : '';
        const isMainnet = networkId.startsWith('solana');
        const network = isMainnet ? 'mainnet' : 'devnet';
        
        const userCollections = await heliusService.getCollectionsByOwner(currentAddress, network, true);
        const formattedCollections = userCollections.map((collection, index) => ({
          name: collection.name,
          image: collection.image || '/assets/placeholder-nft.svg',
          id: `solana-${collection.address}-${index}`, // Add network prefix and index for uniqueness
          count: 0 // We'll update this after fetching NFTs
        }));
        
        // We need NFTs to update collection counts
        const userNFTs = cachedNfts.solana.length > 0 ? cachedNfts.solana : await heliusService.getNFTsByOwner(currentAddress, network, true);
        
        // Update collection counts
        const collectionCounts = new Map<string, number>();
        userNFTs.forEach(nft => {
          if (nft.collection) {
            const count = collectionCounts.get(nft.collection) || 0;
            collectionCounts.set(nft.collection, count + 1);
          }
        });
        
        const collectionsWithCounts = formattedCollections.map(collection => ({
          ...collection,
          count: collectionCounts.get(collection.id) || 0
        }));
        
        // Update current state
        setCollections(collectionsWithCounts);
        
        // Cache the data
        setCachedCollections(prev => ({
          ...prev,
          solana: collectionsWithCounts
        }));
        
        setToast({
          message: "Collections refreshed successfully",
          type: "success"
        });
      } else if (currentNetwork === 'ethereum' && networkStatus.ethereum) {
        // Check if we are disconnecting before making API call
        if (isDisconnecting.current) {
          clearPendingFlag('collections');
          return;
        }
        
        try {
          // Fetch NFTs from ColorFuel API to get collection data with refresh
          const nfts = await nftService.getNFTsByAccount(currentAddress, 'ethereum', 100, true);
          
          // Group NFTs by collection
          const collectionMap = new Map<string, { name: string; count: number; image: string; slug: string }>();
          nfts.forEach(nft => {
            const collection = nft.collection;
            if (!collectionMap.has(collection)) {
              // Use display_image_url as primary image source
              const imageUrl = nft.display_image_url || nft.image_url || '/assets/placeholder-nft.svg';
              
              collectionMap.set(collection, {
                name: collection,
                count: 0,
                image: imageUrl,
                slug: collection.toLowerCase().replace(/\s+/g, '-')
              });
            }
            const collectionData = collectionMap.get(collection)!;
            collectionData.count++;
          });
          
          // Create initial collections array
          const collections = Array.from(collectionMap.entries()).map(([name, data], index) => ({
            name: data.name,
            image: data.image,
            id: `ethereum-${data.name}-${index}`,
            count: data.count,
            slug: data.slug
          }));
          
          // Update current state with collections
          setCollections(collections);
          
          setCachedCollections(prev => ({
            ...prev,
            ethereum: collections
          }));
          
          setToast({
            message: "Collections refreshed successfully",
            type: "success"
          });
        } catch (error: any) {
          if (error.message?.includes("expired")) {
            setToast({
              message: "Connection expired. Please reconnect your wallet",
              type: "error"
            });
            // Optionally trigger wallet reconnection
            if (appKit.open) {
              appKit.open();
            }
          } else {
            setToast({
              message: "Failed to fetch collections. Please try again",
              type: "error"
            });
          }
        }
      }
    } catch (error) {
      setToast({
        message: "Failed to refresh collections",
        type: "error"
      });
    } finally {
      setIsRefreshingCollections(false);
      clearPendingFlag('collections');
      setTimeout(() => setToast(null), 5000);
    }
  };

  return (
    <>
      {/* Overlay that appears behind the panel */}
      {isOpen && (
        <div className="wallet-overlay" onClick={onClose}></div>
      )}
      
      {/* The sliding panel */}
      <div className={`wallet-panel ${isOpen ? 'open' : ''}`}>
        <div className="wallet-panel-header">
          <div className="wallet-header-with-icon">
            <div className="wallet-icon-small">💼</div>
            <h2>Wallet</h2>
          </div>
          <button className="wallet-close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="wallet-panel-content">
          {/* Check if connected to EITHER Solana OR Ethereum network */}
          {isConnected && getCurrentAddress() && (networkStatus.solana || networkStatus.ethereum) ? (
            <div className="wallet-connected-container">
              <div className="wallet-status-section">
                <div className="wallet-status">
                  <span className="status-label">Status</span>
                  <span className="status-value connected">Connected</span>
                </div>
                <button 
                  className="wallet-disconnect-button"
                  onClick={handleDisconnect}
                >
                  Disconnect
                </button>
              </div>
              
              <div className="network-switcher">
                <div className="network-label">Network</div>
                <div className="network-options">
                  <div 
                    className={`network-option ${currentNetwork === 'solana' ? 'active' : ''}`}
                    onClick={() => !switchingNetwork && currentNetwork !== 'solana' ? handleNetworkSwitch('solana') : undefined}
                  >
                    <SolanaLogo />
                    <span>Solana</span>
                    {networkStatus.solana && <span className="network-status-indicator connected"></span>}
                  </div>
                  <div 
                    className={`network-option ${currentNetwork === 'ethereum' ? 'active' : ''}`}
                    onClick={() => !switchingNetwork && currentNetwork !== 'ethereum' ? handleNetworkSwitch('ethereum') : undefined}
                  >
                    <EthereumLogo />
                    <span>Ethereum</span>
                    {networkStatus.ethereum && <span className="network-status-indicator connected"></span>}
                  </div>
                </div>
              </div>
              
              <div className="wallet-address-container">
                {getCurrentAddress() ? (
                  <>
                    <div className="wallet-address-full" title={getCurrentAddress()}>
                      {formatWalletAddress(getCurrentAddress())}
                    </div>
                    <button 
                      className="wallet-copy-button" 
                      onClick={copyAddressToClipboard}
                      title="Copy address"
                    >
                      {showCopied ? '✓' : '📋'}
                    </button>
                  </>
                ) : (
                  <div className="wallet-address-disconnected">
                    Not connected to {currentNetwork.charAt(0).toUpperCase() + currentNetwork.slice(1)} network
                  </div>
                )}
              </div>
              
              <div className="wallet-balance-section">
                <div className="section-header">
                  <h4 className="wallet-section-title">Balance</h4>
                  <button 
                    className="refresh-button" 
                    onClick={handleRefreshBalance}
                    disabled={isRefreshingBalance}
                    title="Refresh balance"
                  >
                    {isRefreshingBalance ? '⟳' : '⟳'}
                  </button>
                </div>
                {!networkStatus[currentNetwork] ? (
                  <div className="network-disconnected-message">
                    Not connected to {currentNetwork.charAt(0).toUpperCase() + currentNetwork.slice(1)} network
                  </div>
                ) : ((isLoading && walletBalance === '0.00000000') || isRefreshingBalance) ? (
                  <div className="loading-indicator">Loading balance...</div>
                ) : (
                  <div className="sol-balance">
                    <span className="sol-amount">{walletBalance}</span>
                    <span className="sol-symbol">{balanceSymbol || (currentNetwork === 'solana' ? 'SOL' : 'ETH')}</span>
                  </div>
                )}
              </div>
              
              <div className="wallet-nfts-section">
                <div className="section-header">
                  <h4 className="wallet-section-title">Your NFTs</h4>
                  <button 
                    className="refresh-button" 
                    onClick={handleRefreshNFTs}
                    disabled={isRefreshingNFTs}
                    title="Refresh NFTs"
                  >
                    {isRefreshingNFTs ? '⟳' : '⟳'}
                  </button>
                </div>
                {!networkStatus[currentNetwork] ? (
                  <div className="network-disconnected-message">
                    Not connected to {currentNetwork.charAt(0).toUpperCase() + currentNetwork.slice(1)} network
                  </div>
                ) : isLoading || isRefreshingNFTs ? (
                  <div className="loading-indicator">Loading NFTs...</div>
                ) : nfts.length > 0 ? (
                  <>
                  <div className="nft-grid">
                      {nfts
                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                        .map((nft: any, index: number) => (
                      <div key={`${currentNetwork}-${nft.id}-${index}`} className="nft-item">
                            <img 
                              src={nft.image || '/assets/placeholder-nft.svg'} 
                              alt={nft.name} 
                              className="nft-image"
                              loading="lazy"
                            />
                        <div className="nft-details">
                          <div className="nft-name" title={nft.name}>{nft.name}</div>
                          {nft.description && (
                            <div className="nft-description" title={nft.description}>
                              {nft.description.length > 30 ? `${nft.description.substring(0, 30)}...` : nft.description}
                            </div>
                          )}
                          {nft.symbol && <div className="nft-symbol">{nft.symbol}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                    
                    {/* Show total NFT count even when there's only one page */}
                    {nfts.length <= itemsPerPage && nfts.length > 0 && (
                      <div className="nft-total-count">
                        Showing {nfts.length} {nfts.length === 1 ? 'NFT' : 'NFTs'}
                      </div>
                    )}
                    
                    {/* Always show pagination controls when there's more than one page of NFTs */}
                    {nfts.length > itemsPerPage && (
                      <div className="pagination-controls">
                        <button 
                          className="pagination-button" 
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <span className="page-indicator">
                          Page {currentPage} of {Math.ceil(nfts.length / itemsPerPage)}
                          {nfts.length > 0 && (
                            <span className="nft-count"> 
                              ({(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, nfts.length)} of {nfts.length})
                            </span>
                          )}
                        </span>
                        <button 
                          className="pagination-button" 
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(nfts.length / itemsPerPage)))}
                          disabled={currentPage === Math.ceil(nfts.length / itemsPerPage)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state">No NFTs found in this wallet</div>
                )}
              </div>
              
              <div className="wallet-collections-section">
                <div className="section-header">
                  <h4 className="wallet-section-title">Your collections</h4>
                  <button 
                    className="refresh-button" 
                    onClick={handleRefreshCollections}
                    disabled={isRefreshingCollections}
                    title="Refresh collections"
                  >
                    {isRefreshingCollections ? '⟳' : '⟳'}
                  </button>
                </div>
                {!networkStatus[currentNetwork] ? (
                  <div className="network-disconnected-message">
                    Not connected to {currentNetwork.charAt(0).toUpperCase() + currentNetwork.slice(1)} network
                  </div>
                ) : isLoading || isRefreshingCollections ? (
                  <div className="loading-indicator">Loading collections...</div>
                ) : collections.length > 0 ? (
                  <div className="collections-list">
                    {collections.map((collection, index) => (
                      <div key={`${currentNetwork}-${collection.id}-${index}`} className="collection-item">
                        <img 
                          src={collection.image} 
                          alt={collection.name} 
                          className="collection-image"
                          loading="lazy" // Add lazy loading for images
                        />
                        <div className="collection-details">
                          <div className="collection-name">{collection.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">No collections found</div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="wallet-icon-large">💼</div>
              <h3 className="wallet-panel-title">Connect Your Wallet</h3>
              <div className="wallet-panel-description">
                Connect your wallet to access all features of ColorFuel
              </div>
              
              <div className="wallet-button-wrapper" onClick={handleWalletConnectClick}>
                <w3m-button />
              </div>
            </>
          )}
          
          <div className="wallet-info">
            <p>By connecting your wallet, you agree to our <Link to="/terms" className="wallet-link" onClick={onClose}>Terms of Service</Link> and <Link to="/privacy" className="wallet-link" onClick={onClose}>Privacy Policy</Link>.</p>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div className="toast-container">
          <div className={`toast ${toast.type}`}>
            <span className="toast-icon">
              {getToastIcon(toast.type)}
            </span>
            {toast.message}
          </div>
        </div>
      )}
    </>
  );
};

export default WalletPanel; 